import employeesApi from 'Api/axios/employees-api';
import { userCourseApi } from 'Api/axios/user-couse-api';
import { userEvaluateCourse } from 'Api/Request';
import { COURSE_STATUS_APPROVED_NAME, S3_EVIDENCES } from 'Constants/General';
import useFetchData from 'Hooks/useFetchData';
import useModal from 'Hooks/useModal';
import useUserCourses from 'Hooks/useUserCourses';
import { useCallback, useEffect, useState } from 'react';
import { s3 } from 'Services/AWS/s3';
import { toast } from 'sonner';
import type { UserDevelopmentPlanI } from 'types';

interface Props {
  userId: string;
}

interface ApproveUserCoursesParams {
  userCourseIds: string[];
  file: File | null;
  evaluationDate?: Date;          // si se pasa, el frontend la envía; si undefined, backend asigna hoy
  courseId: string;
}

const useEmployeeDevelopmentPlan = ({ userId }: Props) => {
  const [selectedCourse, setSelectedCourse] = useState<UserDevelopmentPlanI | null>(null);
  const { open: openApprove, toggle: toggleApprove } = useModal();
  const { open: openResetProgress, toggle: toggleResetProgress } = useModal();
  const { open: openCourseAssign, toggle: toggleCourseAssigner } = useModal();
  const { open: openDeassignConfirmation, toggle: toggleDeassignConfirmation } = useModal();

  const { courses, fetch: fetchAssigned } = useUserCourses({
    init: true,
    userId,
    assigned: true,
    enrolled: true,
  });

  const { data, fetchOne } = useFetchData({
    getOne: id => employeesApi.readDevelopmentPlan(id),
  });

  useEffect(() => {
    fetchOne(userId);
  }, [userId]);

  const uploadEvaluationFileS3 = async (key: string, file: Blob, contentType: string): Promise<boolean> => {
    if (!file) return false;
    try {
      const ok = await s3.uploadFileToBucketWithKeyProvided(key, file as unknown as Buffer, contentType);
      return !!ok;
    } catch (caught) {
      console.error('Error subiendo evidencia a S3:', caught);
      return false;
    }
  };

  /**
   * Aprueba uno o varios userCourses con evidencia compartida y/o fecha retroactiva.
   * Maneja upload S3, mutación GraphQL, rollback y refetch.
   */
  const approveUserCourses = useCallback(
    async ({ userCourseIds, file, evaluationDate, courseId }: ApproveUserCoursesParams): Promise<boolean> => {
      let key: string | undefined;

      if (file) {
        key = `${S3_EVIDENCES}/${userId}/${courseId}/${file.name}`;
        const uploadOk = await uploadEvaluationFileS3(key, file, file.type);
        if (!uploadOk) {
          toast.error('No se pudo subir el archivo de evidencia');
          return false;
        }
      }

      const dedupedIds = Array.from(new Set(userCourseIds));
      let response: { status?: boolean; errorMessage?: string } | undefined;
      try {
        response = await userEvaluateCourse({
          ids: dedupedIds,
          userCourseStatus: COURSE_STATUS_APPROVED_NAME,
          url: key,
          evaluationDate: evaluationDate ? evaluationDate.toISOString() : undefined,
        });
      } catch (err) {
        console.error('Error en userEvaluateCourse:', err);
        if (key) {
          await s3.deleteObject(key);
        }
        toast.error('No se pudo aprobar el curso');
        return false;
      }

      if (!response?.status) {
        if (key) {
          await s3.deleteObject(key);
        }
        toast.error(response?.errorMessage ?? 'No se pudo aprobar el curso');
        return false;
      }

      toast.success(`Curso aprobado a ${dedupedIds.length} empleado(s)`);
      fetchOne(userId);
      fetchAssigned(userId);
      return true;
    },
    [userId, fetchOne, fetchAssigned]
  );

  /**
   * Reset/deassign sin tocar lógica de aprobación.
   */
  const updateUserCourse = async (action: 'reset_progress' | 'deassign'): Promise<boolean> => {
    if (!selectedCourse) return false;

    if (action !== 'reset_progress' && action !== 'deassign') {
      console.warn(`updateUserCourse llamado con acción no soportada: "${action}". Use approveUserCourses para aprobar.`);
      return false;
    }

    if (action === 'reset_progress') {
      const response = await employeesApi.resetUserCourseProgress(userId, selectedCourse.userCourseId);
      if (response.error) {
        toast.error(response.error);
        return false;
      }
      toggleResetProgress(false);
      toast.success('Progreso reiniciado correctamente');
    }

    if (action === 'deassign') {
      const response = await userCourseApi.remove(userId, selectedCourse.Course.id);
      if (response.error) {
        toast.error(response.error);
        return false;
      }
      toggleDeassignConfirmation(false);
      toast.success('Curso desasignado correctamente.');
    }

    fetchOne(userId);
    fetchAssigned(userId);
    return true;
  };

  const assignCourses = useCallback(
    async (data: string[]) => {
      const response = await userCourseApi.post(userId, data);
      if (response.error) {
        toast.error(response.error);
        return;
      }
      toast.success(response.data.message);
      fetchAssigned(userId);
    },
    [userId]
  );

  return {
    selectedCourse,
    setSelectedCourse,
    openApprove,
    toggleApprove,
    openResetProgress,
    toggleResetProgress,
    developmentPlan: data,
    approveUserCourses,
    updateUserCourse,
    openCourseAssign,
    toggleCourseAssigner,
    assignCourses,
    courses,
    openDeassignConfirmation,
    toggleDeassignConfirmation,
  };
};

export default useEmployeeDevelopmentPlan;
